import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X, LogOut, User2 } from 'lucide-react';

const BrandMark = () => (
    <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <g>
            <rect x="5" y="5" width="12" height="12" rx="3" fill="#B486D6" />
            <rect x="19" y="5" width="12" height="12" rx="3" fill="#7B3FA9" />
            <rect x="5" y="19" width="12" height="12" rx="3" fill="#5B2885" />
            <rect x="19" y="19" width="12" height="12" rx="3" fill="#B486D6" />
            <circle cx="11" cy="11" r="2" fill="#FFFDF9" />
            <circle cx="25" cy="11" r="2" fill="#FFFDF9" />
            <circle cx="11" cy="25" r="2" fill="#FFFDF9" />
            <circle cx="25" cy="25" r="2" fill="#FFFDF9" />
        </g>
    </svg>
);

export default function Header() {
    const { user, logout } = useAuth();
    const [open, setOpen] = React.useState(false);
    const navigate = useNavigate();

    const linkCls = ({ isActive }) =>
        `px-3 py-2 rounded-full text-sm font-medium transition-colors ${
            isActive ? 'bg-lq-purple-100 text-lq-purple-900' : 'text-lq-ink-700 hover:bg-lq-purple-50'
        }`;

    return (
        <header className="sticky top-0 z-40 bg-lq-paper/90 backdrop-blur border-b border-lq-ink-100" data-testid="site-header">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
                <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
                    <BrandMark />
                    <span className="font-display text-xl font-semibold text-lq-ink-black tracking-tight">
                        Linguistic<span className="text-lq-purple-500"> Quest</span>
                    </span>
                </Link>
                <nav className="ml-auto hidden md:flex items-center gap-1" aria-label="Primary">
                    <NavLink to="/languages" className={linkCls} data-testid="nav-languages">Languages</NavLink>
                    <NavLink to="/countries" className={linkCls} data-testid="nav-countries">Countries</NavLink>
                    <NavLink to="/pricing" className={linkCls} data-testid="nav-pricing">Pricing</NavLink>
                    <NavLink to="/schools" className={linkCls} data-testid="nav-schools">Schools</NavLink>
                    <NavLink to="/leaderboard" className={linkCls} data-testid="nav-leaderboard">Leaderboard</NavLink>
                    <NavLink to="/faq" className={linkCls} data-testid="nav-faq">FAQ</NavLink>
                    <NavLink to="/help" className={linkCls} data-testid="nav-help">Help</NavLink>
                    {user ? (
                        <>
                            <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">Dashboard</NavLink>
                            <NavLink to="/account" className={linkCls} data-testid="nav-account" aria-label="My account">
                                <User2 size={14} className="inline mr-1" />Account
                            </NavLink>
                            {user.role === 'admin' && (
                                <NavLink to="/admin" className={linkCls} data-testid="nav-admin">Admin</NavLink>
                            )}
                            {(user.role === 'teacher' || user.role === 'admin') && (
                                <NavLink to="/teacher" className={linkCls} data-testid="nav-teacher">Teacher</NavLink>
                            )}
                            <button
                                onClick={() => { logout(); navigate('/'); }}
                                className="ml-2 inline-flex items-center gap-1 text-sm text-lq-ink-700 hover:text-lq-purple-700"
                                data-testid="btn-logout"
                                aria-label="Sign out"
                            >
                                <LogOut size={16} /> Sign out
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="px-4 py-2 text-sm font-medium text-lq-purple-700 hover:text-lq-purple-900" data-testid="nav-login">
                                Sign in
                            </Link>
                            <Link
                                to="/register"
                                className="px-4 py-2 rounded-full text-sm font-semibold bg-lq-purple-500 text-white hover:bg-lq-purple-700 transition-colors"
                                data-testid="nav-register"
                            >
                                Start free
                            </Link>
                        </>
                    )}
                </nav>
                <button
                    onClick={() => setOpen(!open)}
                    className="md:hidden ml-auto p-2 text-lq-ink-700"
                    aria-label="Toggle menu"
                    data-testid="btn-mobile-menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>
            {open && (
                <div className="md:hidden border-t border-lq-ink-100 px-4 py-3 flex flex-col gap-1 bg-lq-paper" data-testid="mobile-menu">
                    <NavLink to="/languages" onClick={() => setOpen(false)} className="py-2 text-lq-ink-900">Languages</NavLink>
                    <NavLink to="/countries" onClick={() => setOpen(false)} className="py-2 text-lq-ink-900">Countries</NavLink>
                    <NavLink to="/pricing" onClick={() => setOpen(false)} className="py-2 text-lq-ink-900">Pricing</NavLink>
                    <NavLink to="/leaderboard" onClick={() => setOpen(false)} className="py-2 text-lq-ink-900">Leaderboard</NavLink>
                    <NavLink to="/faq" onClick={() => setOpen(false)} className="py-2 text-lq-ink-900">FAQ</NavLink>
                    <NavLink to="/help" onClick={() => setOpen(false)} className="py-2 text-lq-ink-900">Help</NavLink>
                    <NavLink to="/schools" onClick={() => setOpen(false)} className="py-2 text-lq-ink-900">Schools</NavLink>
                    <NavLink to="/contact" onClick={() => setOpen(false)} className="py-2 text-lq-ink-900">Contact</NavLink>
                    {user ? (
                        <>
                            <NavLink to="/dashboard" onClick={() => setOpen(false)} className="py-2 text-lq-ink-900">Dashboard</NavLink>
                            <NavLink to="/account" onClick={() => setOpen(false)} className="py-2 text-lq-ink-900">Account</NavLink>
                            {user.role === 'admin' && <NavLink to="/admin" onClick={() => setOpen(false)} className="py-2 text-lq-ink-900">Admin</NavLink>}
                            <button onClick={() => { logout(); setOpen(false); navigate('/'); }} className="py-2 text-left text-lq-error">Sign out</button>
                        </>
                    ) : (
                        <>
                            <NavLink to="/login" onClick={() => setOpen(false)} className="py-2 text-lq-ink-900">Sign in</NavLink>
                            <NavLink to="/register" onClick={() => setOpen(false)} className="py-2 font-semibold text-lq-purple-700">Start free</NavLink>
                        </>
                    )}
                </div>
            )}
        </header>
    );
}
